import React, { useState, useEffect, useCallback } from 'react';
import { useField } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import { IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';

import { TextField as MuiTextField } from '@ccm-office/ccm-ui';

import { IFastTextField } from './types';
import { useStyles } from './style';

const INPUT_CHANGE_DELAY = 500;

function FastTextField({ onChange, changeDelay = INPUT_CHANGE_DELAY, ...rest }: IFastTextField) {
  const classNames = useStyles();

  const { name, helperText, InputProps, disabled } = rest;

  const [field, meta, { setValue }] = useField(name);
  const [innerValue, setInnerValue] = useState('');

  //Update inner value with the formik value
  useEffect(() => {
    if (field.value) {
      setInnerValue(field.value);
    } else {
      setInnerValue('');
    }
  }, [field.value]);

  const debouncedHandleOnChange = useDebouncedCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }
      setValue(event.target.value);
    },
    INPUT_CHANGE_DELAY
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      setInnerValue(newValue);
      debouncedHandleOnChange(event);
    },
    [debouncedHandleOnChange]
  );

  const onClear = () => {
    setValue('');
  };

  const iProps = Object.assign(
    {
      endAdornment: (
        <React.Fragment>
          {field.value && !disabled && (
            <IconButton onClick={onClear}>
              <ClearIcon className={classNames.clearIcon} data-testid="btn-clear-field-value" />
            </IconButton>
          )}
        </React.Fragment>
      ),
    },
    InputProps
  );

  return (
    <MuiTextField
      {...rest}
      variant="outlined"
      value={innerValue}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      InputProps={iProps}
      error={Boolean(meta.touched && meta.error)}
      helperText={(meta.touched && meta.error) || helperText}
    />
  );
}

export default FastTextField;
